import { useState } from "react";

const SUBMIT_URL = "https://readdy.ai/api/form/dagmulptto0ceqqni0cg";

const topics = [
  { value: "follow", label: "后续跟进" },
  { value: "reverse", label: "反转疑云" },
  { value: "progress", label: "最新进展" },
  { value: "result", label: "实锤结果" },
];

interface FormState {
  type: "idle" | "success" | "error";
  msg: string;
}

export default function SubscribeForm() {
  const [state, setState] = useState<FormState>({ type: "idle", msg: "" });
  const [sending, setSending] = useState(false);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (sending) return;
    const form = e.currentTarget;
    const data = new FormData(form);

    // 蜜罐检测：命中时不向服务端发送，仅展示与正常一致的成功反馈
    const honey = String(data.get("website_alt") || "").trim();
    if (honey) {
      setState({ type: "success", msg: "订阅成功，瓜报将尽快送到你邮箱～" });
      form.reset();
      return;
    }
    data.delete("website_alt");

    setSending(true);
    setState({ type: "idle", msg: "" });
    try {
      const params = new URLSearchParams();
      data.forEach((v, k) => params.append(k, String(v)));
      const res = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        /* 非 JSON 响应按原始文本处理 */
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const ok = res.ok && parsed?.code === "OK";
      if (ok) {
        setState({ type: "success", msg: "订阅成功，最新跟进与反转会准时推送到你邮箱～" });
        form.reset();
      } else {
        setState({
          type: "error",
          msg: serverMsg && !/spam/i.test(serverMsg) ? serverMsg : "提交未成功，请稍后再试。",
        });
      }
    } catch {
      setState({ type: "error", msg: "网络似乎开小差了，请稍后重试。" });
    } finally {
      setSending(false);
    }
  }

  return (
    <section
      id="subscribe"
      className="mx-auto max-w-6xl px-4 md:px-6 py-16 md:py-20"
    >
      <div className="overflow-hidden rounded-3xl border border-primary-200/70 bg-gradient-to-br from-primary-100/70 via-background-50 to-secondary-100/60 p-7 md:p-10">
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-2">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-4 py-1.5 text-xs font-semibold text-primary-700">
              <i className="ri-mail-volume-line" /> 订阅 · 每日瓜报
            </span>
            <h2 className="mt-4 font-heading text-3xl text-foreground-950 md:text-4xl">不错过任何下回分解</h2>
            <p className="mt-4 max-w-lg text-sm leading-7 text-foreground-700 md:text-base">
              留下邮箱，选好你关心的方向。每当关注的瓜出现<strong className="font-semibold text-primary-600">后续、反转或最新进展</strong>，
              瓜报都会第一时间提醒你。退订随时、不打扰。
            </p>
          </div>

          <form data-readdy-form onSubmit={handleSubmit} className="w-full">
            <div className="rounded-2xl border border-background-200/70 bg-background-50 p-5 md:p-6">
              <label className="block text-sm font-medium text-foreground-950">订阅邮箱</label>
              <input
                type="email"
                name="email"
                required
                placeholder="you@example.com"
                autoComplete="email"
                className="mt-2 w-full rounded-lg border border-background-200 bg-background-100/50 px-4 py-3 text-sm text-foreground-950 outline-none placeholder:text-foreground-400 focus:border-primary-400 transition-colors"
              />

              <fieldset className="mt-5">
                <legend className="text-sm font-medium text-foreground-950">想优先追更的方向（可多选）</legend>
                <div className="mt-3 flex flex-wrap gap-2">
                  {topics.map((t) => (
                    <label
                      key={t.value}
                      className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-background-200 bg-background-50 px-3.5 py-1.5 text-sm text-foreground-700 has-[:checked]:border-primary-400 has-[:checked]:bg-primary-50 has-[:checked]:text-primary-700 transition-colors"
                    >
                      <input
                        type="checkbox"
                        name="topics"
                        value={t.value}
                        className="h-3.5 w-3.5 accent-primary-500"
                      />
                      {t.label}
                    </label>
                  ))}
                </div>
              </fieldset>

              {/* 防垃圾内容蜜罐字段 */}
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="fp-honey-guard"
              />

              <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
                <button
                  type="submit"
                  disabled={sending}
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 text-background-50 hover:bg-primary-600 px-6 py-3 text-sm font-semibold transition-colors disabled:opacity-60"
                >
                  {sending ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" /> 提交中…
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-fill" /> 订阅瓜报
                    </>
                  )}
                </button>
                <span className="text-xs text-foreground-500">我们不发送垃圾邮件，随时可退订。</span>
              </div>

              {state.type === "success" && (
                <p className="mt-4 flex items-start gap-2 rounded-lg bg-accent-100 px-4 py-3 text-sm text-accent-800">
                  <i className="ri-checkbox-circle-fill mt-0.5" /> {state.msg}
                </p>
              )}
              {state.type === "error" && (
                <p className="mt-4 flex items-start gap-2 rounded-lg bg-primary-100 px-4 py-3 text-sm text-primary-800">
                  <i className="ri-error-warning-fill mt-0.5" /> {state.msg}
                </p>
              )}
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}